import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { toast } from 'react-toastify';
import { BASE_URL, API_BASE_URL, institute_id } from '../config/config';

/**
 * AI Notes Summarizer Page
 * Converts lengthy documents into concise summaries
 */
const AINoteSummarizer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkFeatureAccess, logFeatureUsage } = useSubscription();
  
  const [inputText, setInputText] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [summaryLength, setSummaryLength] = useState('medium');

  useEffect(() => {
    window.scrollTo(0, 0);
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const access = await checkFeatureAccess('notes_generator');
    if (!access.has_access) {
      toast.error(access.message || 'You need to upgrade to use this feature');
      navigate('/subscription/plans');
    }
  };

  const handleSummarize = async () => {
    if (!inputText.trim()) {
      toast.error('Please enter some text to summarize');
      return;
    }

    if (inputText.length < 100) {
      toast.error('Please enter at least 100 characters for meaningful summarization');
      return;
    }

    setLoading(true);
    
    try {
      // Call the AI API
      const response = await fetch(`${API_BASE_URL}/ai-subscription/features/summary/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Institute-ID': user?.institute_id || '',
        },
        body: JSON.stringify({
          text: inputText,
          length: summaryLength,
          user_id: user?.id
        })
      });

      const data = await response.json();

      if (data.success) {
        const aiSummary = data.data.summary;
        setSummary(aiSummary);
        
        // Log usage
        await logFeatureUsage('notes_generator', {
          input_length: inputText.length,
          summary_type: summaryLength,
          summary_length: data.data.summary_length
        });
        
        toast.success('Summary generated!');
      } else {
        toast.error(data.message || 'Failed to generate summary. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate summary. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setInputText('');
    setSummary('');
    toast.success('Cleared');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(summary);
    toast.success('Summary copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/ai/features')}
            className="text-purple-600 hover:text-purple-700 mb-4 flex items-center gap-2"
          >
            ← Back to AI Features
          </button>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            📄 AI Notes Summarizer
          </h1>
          <p className="text-gray-600">
            Transform lengthy notes into concise, structured summaries
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                📝 Your Notes
              </h2>
              {inputText && (
                <span className="text-sm text-gray-500">
                  {inputText.split(/\s+/).filter(w => w).length} words
                </span>
              )}
            </div>

            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste your notes, lecture transcripts, or long documents here...

Example:
- Class notes from today's lecture
- Textbook chapter content
- Research article
- Study material

The AI will extract key points and create a concise summary for you."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[400px] resize-y font-mono text-sm"
              disabled={loading}
            />

            <div className="mt-4 space-y-4">
              {/* Summary Length Option */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Summary Length
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['short', 'medium', 'detailed'].map((length) => (
                    <button
                      key={length}
                      onClick={() => setSummaryLength(length)}
                      disabled={loading}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                        summaryLength === length
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } disabled:opacity-50`}
                    >
                      {length}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSummarize}
                  disabled={loading || !inputText.trim()}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Summarizing...
                    </>
                  ) : (
                    <>
                      ✨ Generate Summary
                    </>
                  )}
                </button>

                <button
                  onClick={handleClear}
                  disabled={loading}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                💡 Summary
              </h2>
              {summary && (
                <button
                  onClick={handleCopy}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  📋 Copy
                </button>
              )}
            </div>

            {summary ? (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border-2 border-purple-200 min-h-[400px]">
                <div className="prose prose-sm sm:prose max-w-none">
                  <div 
                    className="text-gray-700 whitespace-pre-wrap leading-relaxed"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {summary.split('\n').map((line, idx) => {
                      // Handle bold text with **text**
                      const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                      
                      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                        // Bullet point
                        return (
                          <div key={idx} className="ml-4 mb-2" dangerouslySetInnerHTML={{ __html: formattedLine }} />
                        );
                      } else if (line.trim().startsWith('#')) {
                        // Heading
                        return (
                          <h3 key={idx} className="font-bold text-lg mt-4 mb-2 text-purple-900">
                            {line.replace(/^#+\s*/, '')}
                          </h3>
                        );
                      } else if (line.trim()) {
                        // Regular paragraph
                        return (
                          <p key={idx} className="mb-3" dangerouslySetInnerHTML={{ __html: formattedLine }} />
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-gray-500 text-sm">
                  Your AI-generated summary will appear here
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  Paste your notes on the left and click "Generate Summary"
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-3">
            💡 Tips for Best Results
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <span>✓</span>
                <span>Use at least 100 words for meaningful summaries</span>
              </li>
              <li className="flex items-start gap-2">
                <span>✓</span>
                <span>Well-structured text produces better summaries</span>
              </li>
              <li className="flex items-start gap-2">
                <span>✓</span>
                <span>Include headings and bullet points when possible</span>
              </li>
            </ul>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <span>✓</span>
                <span>Short summaries focus on main points only</span>
              </li>
              <li className="flex items-start gap-2">
                <span>✓</span>
                <span>Medium summaries include key details</span>
              </li>
              <li className="flex items-start gap-2">
                <span>✓</span>
                <span>Detailed summaries preserve more context</span>
              </li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AINoteSummarizer;
